import React, { useCallback, useMemo, useRef } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { useIntl } from 'react-intl-next';
import { v4 as uuid } from 'uuid';

import SearchIcon from '@atlaskit/icon/core/migration/search';
import { Box, xcss } from '@atlaskit/primitives';
import { getEscalations, getTeams, getUsers } from '@atlassian/eoc-auto-complete';
import { useResponderNames } from '@atlassian/eoc-responder-names';
import { filterNoOne, type RequestedResponder } from '@atlassian/eoc-schedule-common';

import { REQUESTED_NO_ONE } from '../../../../../../common/constants';
import { messages as commonMessages } from '../../../../../../common/messages';
import { AutoCompleteWithNoOne } from '../../../../../../common/ui/auto-complete-with-noone';
import { type AutoCompleteWithNoOneItem } from '../../../../../../common/ui/auto-complete-with-noone/custom-option/types';
import { List } from '../../../../../../common/ui/list';
import { ListItem } from '../../../../../../common/ui/list/list-item';
import { useMonitorListItems } from '../../../../../../utils/hooks/monitor-list-items';
import { reorderItems } from '../../../../../../utils/list';
import { focus } from '../../../../../../utils/window';

import { Participant } from './participant';
import { ChangeEvent, type ParticipantListProps } from './types';

const listWrapperStyles = xcss({
	marginTop: 'space.050',
});

// NOTE: Implement Focus/Blur (do not focus if focused, do not blur if blured)
const ParticipantList = ({ value, onChange, isDisabled, ...rest }: ParticipantListProps) => {
	const intlApi = useIntl();
	const autoCompleteRef = useRef<HTMLDivElement>(null);

	const responders = useResponderNames(filterNoOne(value));

	const names = useMemo(
		() =>
			value.map(({ id, type }) => {
				const item = responders.find((responder) => responder.id === id) ?? {};

				if ('name' in item) {
					return item.name as string;
				}

				if (type === 'noone') {
					return intlApi.formatMessage(commonMessages.noOne);
				}

				return id;
			}),
		[intlApi, responders, value],
	);

	const reorder = useCallback(
		(source: number, target: number) => {
			onChange(reorderItems(value, source, target), ChangeEvent.Order);
		},
		[onChange, value],
	);

	useMonitorListItems({
		droppableId: 'participants',
		data: value,
		onComplete: ({ startIndex, targetIndex }) => reorder(startIndex, targetIndex),
		property: 'key',
	});

	const onAdd = useCallback(
		(participant: AutoCompleteWithNoOneItem | null) => {
			if (participant) {
				const { value: id, type } = participant;
				const responder = { id, type, deleted: false, key: uuid() };

				onChange([...value, responder], ChangeEvent.Add);
			}
		},
		[onChange, value],
	);

	const onRemove = useCallback(
		(index: number) => {
			const copy = cloneDeep(value);
			copy.splice(index, 1);

			onChange(copy, ChangeEvent.Remove);

			focus(
				copy.length >= 1
					? copy[index > copy.length - 1 ? copy.length - 1 : index].key
					: autoCompleteRef,
			);
		},
		[onChange, value],
	);

	const onReplaceWith = useCallback(
		(responderToReplaceIndex: number, newResponder: RequestedResponder) => {
			const copy = cloneDeep(value);
			copy.splice(
				responderToReplaceIndex,
				1,
				merge({}, newResponder, { deleted: false, key: uuid() }),
			);

			onChange(copy, ChangeEvent.Replace);

			focus(copy[responderToReplaceIndex].key);
		},
		[onChange, value],
	);

	const onReplaceWithNoOne = useCallback(
		(responderToReplaceIndex: number) => {
			const copy = cloneDeep(value);
			copy.splice(
				responderToReplaceIndex,
				1,
				merge({}, REQUESTED_NO_ONE, { deleted: false, key: uuid() }),
			);

			onChange(copy, ChangeEvent.Replace);

			focus(copy[responderToReplaceIndex].key);
		},
		[onChange, value],
	);

	return (
		<>
			<AutoCompleteWithNoOne
				{...rest}
				ref={autoCompleteRef}
				endpoints={[getUsers, getTeams, getEscalations]}
				components={{
					DropdownIndicator: () => (
						<SearchIcon
							color="currentColor"
							spacing="spacious"
							label={intlApi.formatMessage(commonMessages.search)}
						/>
					),
				}}
				isDisabled={isDisabled}
				onChange={onAdd}
				placeholder={intlApi.formatMessage(commonMessages.addParticipants)}
				controlShouldRenderValue={false}
				isOptionSelected={() => false}
				aria-label={intlApi.formatMessage(commonMessages.search)}
			/>

			<Box xcss={listWrapperStyles}>
				<List variant="participant" testId="participant-list-container">
					{value?.map((responder, index) => {
						const { id, type, deleted, key } = responder;

						return (
							<ListItem
								key={key}
								value={key}
								title={names[index]}
								droppableId="participants"
								variant="participant"
								isDisabled={isDisabled}
								index={index}
								length={value.length}
								reorder={reorder}
							>
								<Participant
									identifier={key}
									responder={{ id, type, deleted }}
									isDisabled={isDisabled}
									order={index + 1}
									onRemove={() => onRemove(index)}
									onReplaceWith={(responder) => onReplaceWith(index, responder)}
									onReplaceWithNoOne={() => onReplaceWithNoOne(index)}
								/>
							</ListItem>
						);
					})}
				</List>
			</Box>
		</>
	);
};

export { ParticipantList };
